// email is optional -- carried so ContextSelection can confirm a pending
// signup role (2026-09-16 flow) actually belongs to the email that just
// verified, rather than a leftover choice from an unrelated, unfinished
// signup on the same device. Not every session source populates it.
const SIGN_OUT_WAIT_MS = 4000;

export type AdultSession = {
  user: { id: string; email?: string | null };
  expires_at?: number;
};

type AuthResult = { error: { message: string } | null };
type SessionResult = { data: { session: AdultSession | null }; error: unknown };
type VerifyOtpResult = {
  data: { session: AdultSession | null };
  error: { message: string } | null;
};
export type AdultAuthProvider = {
  signInWithOtp(input: {
    email: string;
    options: {
      shouldCreateUser: boolean;
      emailRedirectTo?: string;
    };
  }): Promise<AuthResult>;
  resetPasswordForEmail(
    email: string,
    options?: { redirectTo?: string },
  ): Promise<AuthResult>;
  signOut(options?: {
    scope?: 'global' | 'local' | 'others';
  }): Promise<AuthResult>;
  getSession(): Promise<SessionResult>;
  // The same underlying request behind signInWithOtp also generates a
  // plain 6-digit code (Supabase always issues both a clickable link and
  // a code for one email-OTP request) -- this lets a learner-adult type
  // that code in directly, as a fallback for when their email provider's
  // link-scanning security feature has already consumed the link before
  // they click it (a real, documented Supabase/email-security
  // interaction, not something fixable by retrying the link).
  verifyOtp(input: {
    email: string;
    token: string;
    type: 'email';
  }): Promise<VerifyOtpResult>;
};

export function createAdultAuthService(
  provider: AdultAuthProvider,
  redirectBase = window.location.origin,
) {
  const submit = async (
    email: string,
    shouldCreateUser: boolean,
  ): Promise<{ status: 'submitted' }> => {
    await provider.signInWithOtp({
      email,
      options: {
        shouldCreateUser,
        emailRedirectTo: `${redirectBase}/auth/callback`,
      },
    });
    return { status: 'submitted' };
  };

  return {
    // Sign-in redesign (2026-09-15): one action for both a returning adult
    // and a brand-new one -- always shouldCreateUser:true, and never sends
    // registration_intent, since role is no longer known at this point
    // (see claim_initial_role, called after this from ContextSelection
    // instead). For an existing email this is just a normal sign-in: the
    // account-creation trigger only ever fires on a genuine new auth.users
    // row, so it never re-fires or reassigns anything for a returning
    // adult.
    continueWithEmail: (email: string) => submit(email, true),
    // DodoSkills has no passwords: "recover access" sends the same sign-in
    // link and code as Log in (never creating an account). It used to call
    // resetPasswordForEmail, whose email said "Reset password".
    async requestRecovery(email: string) {
      return submit(email, false);
    },
    async reauthenticate(email: string) {
      return submit(email, false);
    },
    verifyCode: async (email: string, code: string) => {
      const { data, error } = await provider.verifyOtp({
        email,
        token: code,
        type: 'email',
      });
      if (error || !data.session) return { status: 'invalid' as const };
      return { status: 'verified' as const };
    },
    // Ends the session everywhere if the network answers within a few
    // seconds, and always on this device -- so Sign out can never hang
    // waiting on a slow or dropped connection.
    async signOut() {
      try {
        await Promise.race([
          provider.signOut(),
          new Promise((resolve) => setTimeout(resolve, SIGN_OUT_WAIT_MS)),
        ]);
      } catch {
        // Network failure: still end it on this device below.
      }
      try {
        await provider.signOut({ scope: 'local' });
      } catch {
        // Nothing more to clear.
      }
    },
    // Inactivity sign-out: ends the session on this device only, so an idle
    // laptop doesn't also sign the same person out on their phone.
    async signOutThisDevice() {
      await provider.signOut({ scope: 'local' });
    },
    async restoreSession() {
      const { data, error } = await provider.getSession();
      if (error || !data.session) return null;
      if (
        data.session.expires_at &&
        data.session.expires_at * 1000 <= Date.now()
      )
        return null;
      return data.session;
    },
  };
}

export type AdultAuthService = ReturnType<typeof createAdultAuthService>;
